import { useNavigate } from 'react-router-dom';
import { AgentAvatar, parseSeed, PALETTES } from '../AgentAvatar';
import { formatCurrency } from '../../utils/formatting';

/**
 * Graduation modal — placeholder until POST /portfolio/:paperId/graduate exists.
 * Per wf-v3.jsx GraduationModal.
 * Action buttons link to /plans for now.
 */
export function GraduationModal({ paperPortfolio, paperDetail, isOpen, onClose }) {
  const navigate = useNavigate();

  if (!isOpen || !paperPortfolio) return null;

  const agent = paperPortfolio.active_agent;
  const pnlPct = parseFloat(paperDetail?.realized_pnl_pct || 0);
  const closedCount = paperDetail?.closed_positions?.length || 0;
  const winRate = parseFloat(paperDetail?.win_rate || 0);

  const { avatarSeed, paletteIndex } = agent?.avatar_seed
    ? parseSeed(agent.avatar_seed)
    : { avatarSeed: agent?.name || 'x', paletteIndex: 0 };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 z-40" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-slate-900 border border-slate-700 rounded-xl shadow-2xl w-full max-w-[520px] max-h-[90vh] overflow-y-auto p-6 flex flex-col gap-5">

          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded bg-amber-600 text-white font-bold">graduate</span>
              <h2 className="text-xl font-bold text-white">Promote strategy to live</h2>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white text-sm">
              &#10005; <span className="text-xs text-slate-500">esc</span>
            </button>
          </div>

          {/* Source → Target */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 flex items-center gap-4">
            <div className="flex-shrink-0">
              {agent ? (
                <AgentAvatar
                  name={agent.name}
                  seed={avatarSeed}
                  colors={PALETTES[paletteIndex] || PALETTES[0]}
                  size={48}
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-700" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-white">{paperPortfolio.name}</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-700 text-slate-400">paper</span>
              </div>
              <div className="text-xs text-slate-500">{agent?.name || 'no agent'}</div>
              {paperDetail && (
                <div className="text-xs text-green-400 mt-0.5">
                  {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}% P&L &middot; {closedCount} closed trades &middot; {winRate.toFixed(0)}% win rate
                </div>
              )}
            </div>
            <span className="text-lg text-slate-600 flex-shrink-0">&rarr;</span>
            <div className="flex-shrink-0 text-right">
              <div className="text-sm font-bold text-white">Live HL portfolio</div>
              <div className="text-xs text-slate-500">new &middot; $0 starting</div>
            </div>
          </div>

          {/* What carries over */}
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">What carries over</div>
            <div className="flex flex-wrap gap-2">
              {['agent persona', 'research strategy', 'recommendation prompts', 'model & memory'].map(item => (
                <span key={item} className="text-xs px-2 py-1 rounded bg-slate-800 border border-slate-700 text-slate-300">
                  &#10003; {item}
                </span>
              ))}
            </div>
          </div>

          {/* What doesn't */}
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide mb-2">What doesn't</div>
            <div className="flex flex-wrap gap-2">
              {['paper trade history', 'paper P&L curve', 'open paper positions'].map(item => (
                <span key={item} className="text-xs px-2 py-1 rounded bg-slate-800/50 border border-slate-700/50 text-slate-500">
                  &#10007; {item}
                </span>
              ))}
            </div>
            <p className="text-xs text-slate-500 italic mt-2">
              Live trading starts fresh. Your paper stays untouched as a benchmark you can compare against.
            </p>
          </div>

          {/* PRO plan callout */}
          <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-yellow-300">Requires PRO plan</div>
              <div className="text-xs text-yellow-300/60">$29/mo &middot; 14-day free trial &middot; cancel anytime</div>
            </div>
            <button
              onClick={() => { onClose(); navigate('/plans'); }}
              className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs font-medium transition-colors flex-shrink-0"
            >
              start trial
            </button>
          </div>

          <hr className="border-slate-700 border-dashed" />

          {/* Footer */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">&#8629; enter to confirm</span>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded text-sm transition-colors"
              >
                cancel
              </button>
              <button
                onClick={() => { onClose(); navigate('/plans'); }}
                className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-sm font-medium transition-colors"
              >
                graduate {paperPortfolio.name} &rarr; live
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
